import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT, SERVICES } from "@/lib/content";
import { Reveal, Overline } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border-0 border-b border-[#0B3A82]/20 bg-transparent py-3 font-body text-base text-[#0B3A82] placeholder-[#94a3b8] outline-none transition-colors focus:border-[#D89A18]";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, phone and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent! Our team will contact you shortly.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  const items = [
    { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phoneRaw}` },
    { icon: Phone, label: "Toll Free", value: CONTACT.tollFree, href: `tel:${CONTACT.tollFreeRaw}` },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="mb-16 max-w-2xl">
          <Reveal>
            <Overline>Get In Touch</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-tighter text-[#0B3A82] sm:text-5xl md:text-6xl">
              Request a free quote today.
            </h2>
          </Reveal>
        </div>

        <div className="grid gap-14 lg:grid-cols-[1fr_1fr]">
          {/* Form */}
          <Reveal>
            <form data-testid="enquiry-form" onSubmit={submit} className="space-y-7">
              <div className="grid gap-7 sm:grid-cols-2">
                <input
                  data-testid="enquiry-name"
                  className={inputCls}
                  placeholder="Your Name *"
                  value={form.name}
                  onChange={set("name")}
                />
                <input
                  data-testid="enquiry-phone"
                  className={inputCls}
                  placeholder="Phone Number *"
                  value={form.phone}
                  onChange={set("phone")}
                />
              </div>
              <input
                data-testid="enquiry-email"
                className={inputCls}
                placeholder="Email (optional)"
                value={form.email}
                onChange={set("email")}
              />
              <Select
                value={form.service}
                onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
              >
                <SelectTrigger
                  data-testid="enquiry-service"
                  className="w-full rounded-none border-0 border-b border-[#0B3A82]/20 bg-transparent px-0 py-3 font-body text-base text-[#0B3A82] focus:ring-0"
                >
                  <SelectValue placeholder="Select a Service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.title} value={s.title}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <textarea
                data-testid="enquiry-message"
                className={`${inputCls} resize-none`}
                rows={3}
                placeholder="Tell us about your requirement *"
                value={form.message}
                onChange={set("message")}
              />
              <button
                data-testid="enquiry-submit"
                type="submit"
                disabled={loading}
                className="group flex items-center gap-2 px-8 py-4 font-body text-sm font-semibold uppercase tracking-wider transition-all hover:gap-4 disabled:opacity-60"
                style={{ background: "#D89A18", color: "#0B3A82" }}
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : "Send Enquiry"}
                {!loading && (
                  <ArrowUpRight size={18} className="transition-transform group-hover:rotate-45" />
                )}
              </button>
            </form>
          </Reveal>

          {/* Info + Map */}
          <Reveal delay={0.15}>
            <div className="flex h-full flex-col gap-6">
              <div className="grid gap-px bg-[#0B3A82]/10 sm:grid-cols-3">
                {items.map((it) => (
                  <a
                    key={it.label}
                    href={it.href}
                    data-testid={`contact-${it.label.toLowerCase()}`}
                    className="group flex flex-col gap-2 bg-white p-6 transition-colors hover:bg-[#F8FAFC]"
                  >
                    <it.icon size={20} className="text-[#D89A18]" />
                    <span className="font-body text-xs uppercase tracking-[0.2em] text-[#475569]">
                      {it.label}
                    </span>
                    <span className="font-display text-base font-semibold text-[#0B3A82] group-hover:text-[#D89A18]">
                      {it.value}
                    </span>
                  </a>
                ))}
              </div>

              <div className="flex items-start gap-3 border border-[#0B3A82]/10 bg-[#F8FAFC] p-6">
                <MapPin size={20} className="mt-1 flex-shrink-0 text-[#D89A18]" />
                <p className="font-body text-sm leading-relaxed text-[#0B3A82]">
                  {CONTACT.address.join(", ")}
                </p>
              </div>

              <div className="overflow-hidden" style={{ borderRadius: 12 }}>
                <iframe
                  title="Sanwariya Elevator Location"
                  src={CONTACT.mapEmbed}
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
